import { useMemo, useState, useEffect } from 'react';
import {
  FieldArray,
  Form,
  Formik,
  FormikHelpers,
  getIn,
  type FormikErrors,
} from 'formik';
import { useOutletContext } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch } from '../../../hooks/redux';
import { fetchTasks } from '../../../store/task-slice/apiActions';
import { Roles } from '../../../consts/consts';
import { useAppSelector } from '../../../hooks/redux';
import { activitiesValidation } from './activities-validation-schema';
import { api } from '../../../services/api';
import Btn_transparent from '../../ui/btn_transparent/Btn_transparent';
import ModalCentered from '../../ui/modal-window/ModalCentered';
import i18n from 'i18next';

import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import 'bootstrap/dist/css/bootstrap.min.css';

// import AudienceSelectionWindow from '../AudienceSelectionWindow/AudienceSelectionWindow';

import IconButton from '../../ui/iconButton/IconButton';
import TextInput from '../../ui/input/TextInput';
// import Button from '../../ui/button/Button';
import Checkbox from '../../ui/checkbox/Checkbox';
import InfoTooltip from '../../ui/infoTooltip/infoTooltip';
// import ModalWindow from '../../ui/modal-window/ModalWindow';
import MailingYoutubePlayer from '../../../pages/Mailing/MailingYoutubePlayer/MailingYoutubePlayer';

import { ReactComponent as PlusIcon } from '../../../assets/images/icons/plus.svg';
import { ReactComponent as MinusIcon } from '../../../assets/images/icons/minus.svg';

import sharedStyles from '../index.module.sass';
import { useTranslation } from 'react-i18next';

type FormValues = {
  groups: { id: string; value: string }[];
  amountTo: number | null;
  amountFrom: number;
  startDate: string | null;
  endDate: string | null;
  name: string;
  activities: string[];
  id_bot: number | null;
};

const Activities = () => {
  const { t } = useTranslation();
  const isDisabled = useOutletContext<boolean>();
  const ACTIVITIES = useMemo(
    () => [t('usersParsing.comments'), t('usersParsing.reposts')],
    [],
  );
  const MAX_FIELDS = 5;
  const [isFetching, setIsFetching] = useState(false);
  const [lastDay, setLastDay] = useState('');
  const [modalShow, setModalShow] = useState(false);
  const [error, setError] = useState({ name: '', parsing: '', daily: '' });
  // const [isSelectionActive, setIsSelectionActive] = useState(false);

  const dispatch = useAppDispatch();
  const role = useAppSelector((state) => state.UserData.user?.role.name);
  const currentTariff = useAppSelector((state) => state.Tariff.currentTariff);

  const refreshTasks = () => {
    const limit = 10;
    const offset = 0;
    dispatch(fetchTasks({ limit, offset, operation: 'parsing' }));
  };

  useEffect(() => {
    refreshTasks();
  }, []);

  function getDateAgo(daysAgo: number) {
    const currentDate = new Date();
    const targetDate = new Date(currentDate);
    targetDate.setDate(currentDate.getDate() - daysAgo);
    const yyyy = targetDate.getFullYear();
    const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
    const dd = String(targetDate.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  const handleSetLastDay = () => {
    setLastDay(getDateAgo(1));
  };
  const handleSetLastThreeDay = () => {
    setLastDay(getDateAgo(3));
  };
  const handleSetLastWeek = () => {
    setLastDay(getDateAgo(7));
  };
  const handleSetLastMonth = () => {
    setLastDay(getDateAgo(30));
  };

  const initialValues: FormValues = {
    groups: [{ id: uuidv4(), value: '' }],
    amountTo: null,
    amountFrom: 1,
    startDate: getDateAgo(7),
    endDate: getDateAgo(0),
    name: '',
    activities: [t('usersParsing.comments'), t('usersParsing.reposts')],
    id_bot: null,
  };

  const validateStepOne = (
    values: FormValues,
    errors: FormikErrors<FormValues>,
  ) => {
    return values.groups[0].value.length > 0 && !errors.groups;
  };

  const validateStepTwo = (values: FormValues) => {
    return values.amountFrom >= 1 && values.amountFrom <= values.groups.length;
  };

  const validateStepThree = (
    values: FormValues,
    errors: FormikErrors<FormValues>,
  ) => {
    return values.activities.length > 0 && !errors.activities;
  };

  const validateStepFour = (errors: FormikErrors<FormValues>) => {
    return errors.endDate || errors.startDate ? false : true;
  };

  const validateStepFive = (
    values: FormValues,
    errors: FormikErrors<FormValues>,
  ) => {
    return values.name && !errors.name;
  };

  const handleSubmit = (
    values: FormValues,
    actions: FormikHelpers<FormValues>,
  ) => {
    setIsFetching(true);
    api
      .post('/telegram/parser/activemembers', {
        task_name: values.name.replaceAll(' ', ''),
        parsed_chats: values.groups.map((group) => group.value),
        from_date: lastDay || values.startDate,
        to_date: values.endDate,
        activity_count: values.amountFrom,
        activity: {
          comments: values.activities.includes(t('usersParsing.comments')),
          reposts: values.activities.includes(t('usersParsing.reposts')),
        },
        id_bot: values.id_bot,
        export_file_ext: 'xlsx',
        //TODO
        rerun: false,
      })
      .then(() => {
        actions.resetForm();
        setModalShow(true);
        setError({ name: '', parsing: '', daily: '' });
        refreshTasks();
      })
      .catch((e) => {
        console.error(e);
        if (e.response.data.detail === t('usersParsing.taskAlreadyExists')) {
          setError({ ...error, name: e.response.data.detail });
        } else if (
          e.response.data.detail ===
          t('usersParsing.simultaneousParsersLimitExceeded')
        ) {
          setError({ ...error, parsing: e.response.data.detail });
        } else if (
          e.response.data.detail === t('usersParsing.dailyParsersLimitExceeded')
        ) {
          setError({ ...error, daily: e.response.data.detail });
        }
      })
      .finally(() => {
        setIsFetching(false);
      });
  };

  return (
    <section>
      <ModalCentered
        show={modalShow}
        headerText={t('usersParsing.parsingStarted')}
        bodyText={t('usersFiltration.parsingResultsInHistoryTable')}
        btnText={t('usersParsing.close')}
        onHide={() => setModalShow(false)}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={activitiesValidation}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          touched,
          handleChange,
          handleBlur,
          values,
          setFieldValue,
        }) => (
          <Form className={sharedStyles.form}>
            <a
              className={sharedStyles.instruction__link}
              href={
                i18n.language === 'ru'
                  ? require('../../../assets/pdfs/parsing-activities.pdf')
                  : require('../../../assets/pdfs/parsing-activities_en.pdf')
              }
              target="_blank"
            >
              {t('usersParsing.instructionActivity')}
            </a>
            <MailingYoutubePlayer videoID="ZILbjFQwKUs?si=lN8k2XqOyeG-mYyC" />
            <div
              className={`${sharedStyles.fieldValidationBorder} ${
                validateStepOne(values, errors) ? sharedStyles.success : ''
              } ${errors.groups ? sharedStyles.errors : ''}`}
            >
              <div className={sharedStyles.fieldWrapper}>
                <FieldArray
                  name="groups"
                  render={({ form, push, remove }) => (
                    <>
                      <InfoTooltip
                        text={t('usersFiltration.parsingDescription')}
                      />
                      <div className={sharedStyles.groupsHeader}>
                        <h3 className={sharedStyles.header}>
                          {t('usersParsing.channelsAndGroupsToSearch')}
                        </h3>
                      </div>
                      <div className={sharedStyles.groupsInputsWrapper}>
                        <span style={{ fontSize: '12px', color: '#8586A5' }}>
                          <strong style={{ color: '#B63940' }}>
                            {t('usersParsing.mandatory')}
                          </strong>
                          {t('usersParsing.channelMustBeOpen')}
                          <br /> {t('usersParsing.exampleLink')}
                        </span>
                        {values.groups.map((group, index) => (
                          <div
                            className={sharedStyles.groupsInput}
                            key={group.id}
                          >
                            <TextInput
                              name={`groups.${index}.value`}
                              type="text"
                              placeholder={t('usersParsing.insertLink')}
                              onBlur={handleBlur}
                              value={values.groups[index].value}
                              onChange={handleChange}
                              errorMessage={(() => {
                                if (typeof errors.groups !== 'object')
                                  return '';
                                if (typeof errors.groups[index] !== 'object')
                                  return '';
                                if (getIn(form.touched, `groups.${index}`)) {
                                  return (
                                    errors.groups[index] as { value: string }
                                  ).value;
                                }
                              })()}
                            />
                            {index > 0 ? (
                              <IconButton
                                className={sharedStyles.minusIcon}
                                onClick={() => {
                                  remove(index);
                                }}
                              >
                                <MinusIcon />
                              </IconButton>
                            ) : null}
                          </div>
                        ))}
                        <IconButton
                          disabled={values.groups.length >= MAX_FIELDS}
                          onClick={() => push({ id: uuidv4(), value: '' })}
                        >
                          <div className={sharedStyles.createFieldBtn}>
                            <PlusIcon />
                            {t('usersParsing.addField') +
                              ` (${values.groups.length}/${MAX_FIELDS})`}
                          </div>
                        </IconButton>
                      </div>
                    </>
                  )}
                />
              </div>
            </div>
            <div
              className={`${sharedStyles.fieldValidationBorder} ${
                validateStepOne(values, errors) && validateStepTwo(values)
                  ? sharedStyles.success
                  : ''
              } ${errors.amountFrom ? sharedStyles.errors : ''}`}
            >
              <div className={sharedStyles.fieldWrapper}>
                <h3 className={sharedStyles.header}>
                  {t('usersParsing.numberOfActivities')}
                </h3>
                <InfoTooltip text={t('usersParsing.groupsCount')} />
                <div className={sharedStyles.inputGroup}>
                  <div className={sharedStyles.fieldQty}>
                    <button
                      onClick={() =>
                        setFieldValue('amountFrom', values.amountFrom - 1)
                      }
                      style={{
                        cursor: 'pointer',
                        fontSize: '16px',
                      }}
                      className={
                        values.amountFrom <= 0 ? sharedStyles.hidden : ''
                      }
                    >
                      &ndash;
                    </button>
                    <input
                      className={sharedStyles.inputQty}
                      value={values.amountFrom || 0}
                      onChange={(e) => {
                        setFieldValue(
                          'amountFrom',
                          Number(e.target.value) <= values.groups.length
                            ? Number(e.target.value)
                            : values.groups.length,
                        );
                      }}
                      name="amountFrom"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setFieldValue('amountFrom', values.amountFrom + 1)
                      }
                      className={`${sharedStyles.incDecButton} ${
                        values.amountFrom >= values.groups.length
                          ? sharedStyles.disabled
                          : ''
                      }`}
                      disabled={values.amountFrom >= values.groups.length}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div
              className={`${sharedStyles.fieldValidationBorder} ${
                validateStepOne(values, errors) &&
                validateStepTwo(values) &&
                validateStepThree(values, errors)
                  ? sharedStyles.success
                  : ''
              } ${errors.activities ? sharedStyles.errors : ''}`}
            >
              <div className={sharedStyles.fieldWrapper}>
                <h3 className={sharedStyles.header}>
                  {t('usersParsing.whichActivitiesToConsider')}
                </h3>
                <InfoTooltip text={t('usersParsing.specifyActivities')} />
                <div className={sharedStyles.activities}>
                  {ACTIVITIES.map((activity) => (
                    <Checkbox
                      name="activities"
                      key={activity}
                      title={activity}
                      value={activity}
                      checkboxHandler={handleChange}
                      checked={values.activities.includes(activity)}
                    />
                  ))}
                </div>
                <div className={sharedStyles.errorMessage}>
                  {errors.activities && touched.activities
                    ? errors.activities
                    : null}
                </div>
              </div>
            </div>
            <div
              className={`${sharedStyles.fieldValidationBorder} ${
                validateStepOne(values, errors) &&
                validateStepTwo(values) &&
                validateStepThree(values, errors) &&
                validateStepFour(errors)
                  ? sharedStyles.success
                  : ''
              } ${
                errors.endDate || errors.startDate ? sharedStyles.errors : ''
              }`}
            >
              <div className={sharedStyles.fieldWrapper}>
                <h3 className={sharedStyles.header}>
                  {t('usersParsing.period')}
                </h3>
                <InfoTooltip text={t('usersParsing.selectPeriod')} />
                <div className={sharedStyles.periodWrapper}>
                  <div className={sharedStyles.inputGroup}>
                    <input
                      className={sharedStyles.inputDate}
                      name="startDate"
                      type="date"
                      value={lastDay || values.startDate || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <input
                      className={`custom-calendar-input ${sharedStyles.inputDate}`}
                      name="endDate"
                      type="date"
                      value={values.endDate || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                  <ButtonGroup size="sm" className={sharedStyles.buttonGroup}>
                    <Button
                      variant="outline-secondary"
                      className={sharedStyles.customBtnSecondary}
                      onClick={handleSetLastDay}
                      style={{ padding: '1px 1px', fontSize: '12px' }}
                    >
                      {t('usersParsing.day')}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className={sharedStyles.customBtnSecondary}
                      onClick={handleSetLastThreeDay}
                      style={{
                        padding: '1px 1px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t('usersParsing.3days')}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className={sharedStyles.customBtnSecondary}
                      onClick={handleSetLastWeek}
                      style={{ padding: '1px 1px', fontSize: '12px' }}
                    >
                      {t('usersParsing.week')}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      className={sharedStyles.customBtnSecondary}
                      onClick={handleSetLastMonth}
                      style={{ padding: '1px 1px', fontSize: '12px' }}
                    >
                      {t('usersParsing.month')}
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            </div>
            {/*загрузка файла csv для отслеживания активностей конкретных пользователей в группах */}
            {/* <div>
              <h3 className={sharedStyles.header}>
               {t('usersParsing.usersToTrack')}
              </h3>
              <ModalWindow
                isActive={isSelectionActive}
                setActive={setIsSelectionActive}
              >
                <AudienceSelectionWindow />
              </ModalWindow>
              <Button
                // disabled
                style={{ maxWidth: '610px' }}
                variant="additional"
                onClick={() => setIsSelectionActive(true)}
              >
                {t('usersParsing.selectAudience')}
              </Button>
              <div className={sharedStyles.selectedTasks}></div>
            </div> */}
            <div
              className={`${sharedStyles.fieldValidationBorder} ${
                validateStepOne(values, errors) &&
                validateStepTwo(values) &&
                validateStepThree(values, errors) &&
                validateStepFour(errors) &&
                validateStepFive(values, errors)
                  ? sharedStyles.success
                  : ''
              } ${errors.name ? sharedStyles.errors : ''}`}
            >
              <div className={sharedStyles.fieldWrapper}>
                <h3 className={sharedStyles.header}>{t('common.taskName')}</h3>
                <InfoTooltip text={t('usersParsing.parsingNameHint')} />
                <TextInput
                  style={{ maxWidth: '610px' }}
                  name="name"
                  type="text"
                  placeholder={t('usersParsing.taskName')}
                  hintMessage={t('usersParsing.titleVisibleOnlyToYou')}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={
                    error.name ||
                    (errors.name && touched.name ? errors.name : '')
                  }
                />
                {role === Roles.Admin || role === Roles.SuperUser ? (
                  <TextInput
                    style={{ maxWidth: '610px' }}
                    name="id_bot"
                    type="text"
                    placeholder={t('usersParsing.enterBotId')}
                    value={values.id_bot || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                ) : null}
              </div>
            </div>
            {error.parsing && (
              <p
                className={sharedStyles.errorMessage}
                style={{ margin: '0 auto 16px', textAlign: 'center' }}
              >
                {`${error.parsing}. ${t('usersParsing.tariffLimit')} - ${
                  currentTariff.options.simultaneous_parsing
                }. ${t('usersParsing.waitForTask')}`}
              </p>
            )}
            {error.daily && (
              <p
                className={sharedStyles.errorMessage}
                style={{ margin: '0 auto 16px', textAlign: 'center' }}
              >
                {`${error.daily}. ${t('usersParsing.tariffLimit')} - ${
                  currentTariff.options.parsers_per_day
                }.`}
              </p>
            )}
            <Btn_transparent
              style={{ maxWidth: '610px' }}
              variant={'standart'}
              type="submit"
              disabled={isFetching || isDisabled}
            >
              {isDisabled
                ? t('usersParsing.notAvailableForYourTariff')
                : t('usersParsing.startCollectingAudience')}
            </Btn_transparent>
          </Form>
        )}
      </Formik>
    </section>
  );
};

export default Activities;
